import { Box, Card, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useMemo, useRef } from 'react';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, Loading, toaster, useValidatedSorting, type ConfirmModalHandle, type ItemAction } from '@shared';
import { SmsMessagesService, useDeleteMultipleSmsMessages } from '@admin';
import { useNavigate, useParams } from 'react-router';
import { createColumnHelper } from '@tanstack/react-table';
import Message from './message/Message';
import { useQueryClient } from '@tanstack/react-query';
import { useGetSingleSmsMessage } from '@admin';

const columnHelper = createColumnHelper<SmsMessage>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function DraftsContainer() {
	const { id } = useParams();
	const smsMessageID = id ? parseInt(id, 10) : undefined;

	const { data: message, isFetching: isFetchingGetSingleSms } = useGetSingleSmsMessage(smsMessageID as number, {
		isEnabled: smsMessageID ? true : false,
		getRelated: 'smsMessageGroups,smsMessageManualContacts',
		scopeWith: 'expectedRecipientCount',
		getAttributes: 'expectedCreditUsage',
	});

	if (isFetchingGetSingleSms && !message)
		return (
			<Flex justifyContent="center" alignItems="center" height="100vh">
				<Loading />
			</Flex>
		);

	if (message) {
		return <Message message={message} />;
	}

	return <Drafts />;
}

function Drafts() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<SmsMessage>(),
		[],
	);

	const columns = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('smsMessageGroups', {
			header: t('general.receiver_groups'),
			enableSorting: false,
			cell: ({ row }) => {
				return <Text>{row.original.smsMessageGroups?.length}</Text>;
			},
		}),
		columnHelper.accessor('smsMessageManualContacts', {
			header: t('general.receiver_manual_contacts'),
			enableSorting: false,
			cell: ({ row }) => {
				return <Text>{row.original.smsMessageManualContacts?.length}</Text>;
			},
		}),
		columnHelper.accessor('sender', {
			header: t('general.sender'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	useValidatedSorting('smsMessages', columns);

	const { mutate: deleteMultipleSmsMessages } = useDeleteMultipleSmsMessages();

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const selectedItemsActions: Array<ItemAction<SmsMessage>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					smsMessages: ids.map(id => {
						return {
							smsMessageID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_messages'),
					content: t('general.really_remove_messages'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleSmsMessages(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['smsMessages'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<Card.Root maxWidth={'2000px'} width="100%" flex={1} minWidth={'340px'}>
			<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
				<Flex display={'flex'} flexDirection={'column'} gap={2} flex={1} maxWidth={'100%'}>
					<DataCollectionContextProvider
						dataKey="smsMessages"
						keyAttribute="smsMessageID"
						emptyDataText={t('general.no_drafts_found')}
						fetchFn={(...args) => SmsMessagesService.getAllResources(...args)}
						queryParams={{ filters: 'sendingStatus:eq:draft', getRelated: 'smsMessageGroups,smsMessageManualContacts' }}
						selectedItemsActions={selectedItemsActions}
						initialItemsPerPage={25}
						columns={columns}
						defaultHiddenColumns={[]}
						isServerSide={false}
						defaultSort={{ id: 'timeCreated', desc: true }}
						onClickRow={row => {
							navigate(`/messages/drafts/${row.original.smsMessageID}`);
						}}
					>
						<Flex gap={2} flexWrap="wrap" flexShrink={0}>
							<DataCollectionTableHeader />
						</Flex>
						<Box flex={1} overflow="auto" minH={0}>
							<DataCollectionTable />
						</Box>

						<Flex gap={2}>
							<DataCollectionTableFooter />
							<ConfirmModal ref={confirmRemoveRef} />
						</Flex>
					</DataCollectionContextProvider>
				</Flex>
			</Card.Body>
		</Card.Root>
	);
}
